"use client"

import * as React from "react"
import { useReducedMotion } from "motion/react"

import { AskLeoButton } from "@/components/ask-leo-button"
import { useLeoAmbience } from "@/components/leo-ambience-context"
import {
  LeoCursorDots,
  useLeoCursorSpotlight,
} from "@/components/leo-cursor-dots"
import { FindingCopyEmphasis } from "@/components/question-quality/finding-copy-emphasis"
import {
  QuestionLeoUsagePerformanceHover,
} from "@/components/question-quality/question-leo-performance-insight"
import { LeoThinkingWidget } from "@/components/leo-thinking-widget"
import { LEO_BLOB_ANIM_RATE } from "@/lib/leo-ambience"
import {
  LEO_USAGE_PHRASE_RE,
  leoJobsNote,
  leoJobsNoteEmphasis,
} from "@/lib/question-quality"
import type {
  LeoAuthoringJob,
  QuestionQualityHistory,
} from "@/lib/question-quality/types"
import { cn } from "@/lib/utils"

/** Compact Leo spotted card — smaller radius than the Ask Leo rail wash. */
const TOUCHPOINT_DOT_RADIUS = 72

function LeoJobsNote({
  note,
  history,
  jobs,
}: {
  note: string
  history: QuestionQualityHistory | null
  jobs: LeoAuthoringJob[]
}) {
  const phrases = leoJobsNoteEmphasis(jobs)

  if (!history?.sat || !LEO_USAGE_PHRASE_RE.test(note)) {
    return <FindingCopyEmphasis text={note} phrases={phrases} />
  }

  const parts = note.split(LEO_USAGE_PHRASE_RE)
  return (
    <span>
      {parts.map((part, index) => {
        if (!part) return null
        if (LEO_USAGE_PHRASE_RE.test(part)) {
          return (
            <QuestionLeoUsagePerformanceHover
              key={`${part}-${index}`}
              phrase={part}
              history={history}
            />
          )
        }
        return (
          <FindingCopyEmphasis
            key={`${part}-${index}`}
            text={part}
            phrases={phrases}
          />
        )
      })}
    </span>
  )
}

export function QuestionLeoJobs({
  jobs,
  history = null,
  disabled,
  onAskLeo,
}: {
  jobs: LeoAuthoringJob[]
  history?: QuestionQualityHistory | null
  disabled?: boolean
  onAskLeo: () => void
}) {
  const { prefs } = useLeoAmbience()
  const reduceMotion = useReducedMotion() ?? false
  const showDots = Boolean(prefs.idleDots && !reduceMotion)
  const [opening, setOpening] = React.useState(false)
  const openingTimerRef = React.useRef(0)

  React.useEffect(
    () => () => window.clearTimeout(openingTimerRef.current),
    [],
  )

  const {
    spotlight,
    onPointerMove,
    onPointerEnter,
    onPointerLeave,
    onPointerDown,
  } = useLeoCursorSpotlight(showDots, {
    pulseWaves:
      (prefs.blobAnimations && !reduceMotion) || (opening && !reduceMotion),
    pulseIntervalMs: Math.round(
      1600 * LEO_BLOB_ANIM_RATE[prefs.blobAnimationSpeed],
    ),
  })

  const handleAskLeo = React.useCallback(() => {
    if (disabled || opening) return
    setOpening(true)
    onAskLeo()
    window.clearTimeout(openingTimerRef.current)
    openingTimerRef.current = window.setTimeout(() => setOpening(false), 680)
  }, [disabled, onAskLeo, opening])

  if (jobs.length === 0) return null

  const note = leoJobsNote(jobs)
  const field = {
    ...spotlight,
    x: spotlight.active ? spotlight.x : 22,
    y: spotlight.active ? spotlight.y : 78,
    active: true,
  }

  return (
    <div
      role="group"
      aria-label={`Ask Leo. ${note}`}
      onPointerMove={onPointerMove}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      className={cn(
        "relative isolate w-full overflow-hidden rounded-xl border border-brand/25 bg-clip-padding p-3.5 text-left shadow-xs",
        "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--brand-color)_10%,var(--background)),color-mix(in_oklch,var(--brand-color)_4%,var(--background)))]",
        disabled && "opacity-60",
      )}
    >
      {showDots ? (
        <LeoCursorDots
          spotlight={field}
          radius={TOUCHPOINT_DOT_RADIUS}
          density={prefs.idleDensity}
          grainScale={1.35}
          className="z-[1]"
        />
      ) : null}
      <span className="relative z-[2] flex flex-col gap-3">
        <span className="text-[0.8125rem] leading-5 text-foreground">
          <LeoJobsNote note={note} history={history} jobs={jobs} />
        </span>
        {opening && !reduceMotion ? (
          <LeoThinkingWidget />
        ) : (
          <AskLeoButton
            size="sm"
            variant="outline"
            showShortcut={false}
            animatedStar
            aria-busy={opening}
            className="w-fit bg-transparent"
            disabled={disabled}
            onClick={handleAskLeo}
          />
        )}
      </span>
    </div>
  )
}
